<template>
  <k-modal class="k-user-search-modal" @close="close" :show="show">
    <template #header>
      <h3>KATE Users</h3>
    </template>
    <template #body>
      <div class="k-user-search-body">
        <k-toggle class="exact-search" v-model="exactSearch" label="Use exact search to match the entire search term, including spaces" />
        <div class="k-user-search-input-wrapper">
          <label for="user-search-input">Search users:</label>
          <div class="k-user-search-search-bar">
            <input
              id="user-search-input"
              class="form-control ph-no-capture"
              type="text"
              placeholder="Enter name here..."
              v-model="filterText"
              v-on:keyup.enter="userSearch"
              :aria-invalid="!isvalidSearch"
              aria-describedby="user-search-input-error"
            >
            <span class="loader-icon"><i class="fa fa-spinner fa-spin spin-icon" v-if="loading" aria-hidden="true"></i></span>
            <button class="btn btn-primary" @click="handleSearch" :disabled="loading"><i class="fa fa-search" aria-hidden="true"></i> Search</button>
          </div>
          <small
            v-if="showError && !isvalidSearch"
            id="user-search-input-error"
            class="field-error"
            aria-live="polite"
          >
            Enter at least 2 characters
          </small>
        </div>
        <div v-if="showTable" class="search-table-container">
          <span class="search-count">{{ searchCount }}</span>
          <k-table
            class="search-table"
            :headers="kableHeaders"
            :rows="kableRows"
            :hideable="false"
            :show-controls="false"
            :panel="false"
            :max="10"
            :loading="loading"
            :support-row-click="true"
            @row-clicked="handleRowClick"
          />
        </div>
      </div>
    </template>
  </k-modal>
</template>

<style>
.k-user-search-modal .modal-body {
  margin-bottom: 0;
}

.k-user-search-modal .modal-container {
  min-width: 60%;
}

.k-user-search-toggle {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.k-user-search-modal .search-table td.k-table-key-name a {
  color: var(--kate-type-link);
  cursor: pointer;
  transition: color 0.3s ease-in-out;
  text-decoration: underline;
  line-height: inherit;
  padding: 0;
}

.k-user-search-modal .search-table td.k-table-key-name a:hover {
  color: var(--kate-type-link-hover);
  background-color: transparent;
}

.form-required .k-user-search-modal label::after {
  content: none;
}
</style>

<style scoped>
/* Search */
.spin-icon {
  position: relative;
  right: 30px;
  color: var(--kate-type-primary);
}

.k-user-search-search-bar span {
  width: 0;
}

.k-user-search-input-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100px;
  margin-bottom: 10px;
}

.k-user-search-search-bar {
  text-align: left;
  position: relative;
  display: flex;
  align-items: center;
}

.k-user-search-search-bar label {
  width: 100%;
  margin: 0;
}

.k-user-search-search-bar input {
  margin: 0 auto;
  height: 38px;
  font-size: 1em;
  width: 100%;
  display: block;
  padding: 5px;
  border: var(--input-border);
  color: var(--kate-type-light);
  background-color: var(--input-background);
  outline: 0;
}

.k-user-search-search-bar button {
  height: 38px;
  margin-left: 10px;
}

nav.navbar div ul.nav li .k-user-search-search-bar button:hover {
  background-color: var(--kate-button-primary-hover);
  color: var(--kate-type-light);
  border-radius: 8px;
}

[data-theme="light"] nav.navbar div ul.nav li .k-user-search-search-bar button:hover {
  background-color: var(--kate-button-primary-hover) !important;
  color: var(--kate-type-white) !important;
}

.k-user-search-count {
  text-align: center;
}

.k-user-search-count h5 {
  margin-top: 5px;
  margin-right: 0;
  margin-bottom: 0;
  margin-left: 0;
}

.search-count {
  color: var(--kate-type-secondary);
}

.search-table-container {
  text-align: left;
}

.exact-search {
  padding: 5px 0;
  margin-bottom: 10px;
}

.field-error {
  display: block;
  margin-top: 4px;
  padding: 0 5px;
  color: var(--kate-type-danger);
}
</style>

<script>
import KModal from '@base-components/k-modal.vue';
import KTable from '@base-components/tables/display/k-table.vue';
import { formatId } from '@utils/format-user-id';
import KToggle from '@base-components/forms/k-toggle.vue';

export default {
  components: {
    'k-modal': KModal,
    'k-table': KTable,
    'k-toggle': KToggle,
  },

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    displayUserLinks: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      filterText: '',
      searchedText: '',
      searchedUsers: [],
      loading: false,
      selectedUser: [],
      showTable: false,
      exactSearch: false,
      showError: false,
    };
  },

  watch: {
    show() {
      this.clearFiltersAndResults();
      this.loading = false;
    },
    selectedUser() {
      this.$emit('input', this.selectedUser);
    },
  },

  computed: {
    isvalidSearch() {
      return this.filterText.length >= 2 && this.filterText.length > 0;
    },
    searchCount() {
      if (this.searchedText === '') {
        return 'Please enter a search term';
      } if (this.searchedUsers.length === 0) {
        return 'No users found, try a broader search';
      } if (this.searchedUsers.length === 1) {
        return 'Found 1 user';
      } if (this.searchedUsers.length >= 250) {
        return 'Found 250+ users, please refine your search';
      }
      return `Found ${this.searchedUsers.length} users`;
    },
    searchFoundUsers() {
      if (this.searchedUsers.length > 0) {
        return true;
      }
      return false;
    },
    filterTextParameters() {
      if (this.exactSearch) {
        return this.filterText.toLowerCase();
      }
      return this.filterText.split(' ').join(',').toLowerCase();
    },
    apiEndpoint() {
      return `/api/profile/users/search?terms=${this.filterTextParameters}`;
    },

    kableHeaders() {
      return {
        id: {
          name: 'User ID',
          type: 'string',
          sortable: true,
          displayAs: value => formatId(value),
        },
        name: {
          name: 'Name',
          sortable: true,
          filterable: true,
          type: this.displayUserLinks ? 'url' : 'string',
        },
      };
    },
    kableRows() {
      return this.searchedUsers.length > 0 ? this.searchedUsers.map(u => ({
        id: u.user_id,
        name: {
          text: u.full_name,
          path: { name: 'dashboard_students', params: { studentId: u.user_id } },
        },
      })) : [];
    },
  },

  methods: {
    clearFiltersAndResults() {
      this.filterText = '';
      this.searchedText = '';
      this.searchedUsers = [];
      this.showTable = false;
    },
    close() {
      this.clearFiltersAndResults();
      this.$emit('close');
    },
    handleSearch() {
      if (!this.isvalidSearch) {
        this.showError = true;
        return;
      }
      this.showError = false;
      this.userSearch();
    },
    userSearch() {
      this.loading = true;
      this.$logger.info('Searching for users', undefined, true);
      this.$http.get(this.apiEndpoint).then(res => {
        this.$logger.info('Successfully searched for users');
        this.searchedUsers = res.data;
      }).catch(err => {
        this.$logger.error('Error searching for users', undefined, err);
      }).then(() => {
        this.loading = false;
        this.searchedText = this.filterText;
        this.showTable = true;
      });
    },

    handleRowClick(row) {
      if (!this.displayUserLinks) {
        this.$emit('select', row.id);
      }
    },
  },
};
</script>
